<template src="./related-reviews-list.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import { callTurnToConfigFn } from 'util/turnto-utils'
import { debounce } from 'util/ts-utils'
import domUtils from 'util/dom-utils'
import globalConfig from 'init/global.config'
import globalStore, { Store } from 'init/global-store'
import logger from 'util/logger'
import PaginateMixin from 'mixins/PaginateMixin.vue'
import GridLayoutDataMixin from 'layouts/grid/GridLayoutDataMixin.vue'
import relatedReviewsListService from 'services/api/related-reviews-list'
import { RestError } from 'services/api-service'
import Review from 'components/review/Review.vue'
import reviewsListConfig from 'widgets/reviews-list/reviews-list-widget.config'
import reviewsListUtils from '../../util/reviews-list-utils'

export default {
  data() {
    return {
      originalReviews: new Map<number, unknown>(), // holds the review by id when translated
      previousSku: '',
      reviews: [],
      showMoreDisabled: false,
      globalStoreState: globalStore.state,
      total: 0
    }
  },
  emits: ['openMediaModal', 'relatedReviewsLoaded'],
  components: {
    'tt-review': Review
  },
  props: {
    showFullReview: {
      type: Boolean,
      default: true
    }
  },
  mixins: [GridLayoutDataMixin, PaginateMixin],
  computed: {
    currentSku(): string {
      return (this.globalStoreState as Store).sku
    },
    heading() {
      return { text: this.$msg('relatedReviewsList.header'), level: reviewsListConfig.headingLevel }
    },
    mediaSize() {
      return this.reviewsListConfig.breakpoints[this.currentLayoutIndex || 0].mediaSize
    },
    reviewsListConfig() {
      return reviewsListConfig
    },
    sortBy() {
      return reviewsListConfig.defaultSortOption
    },
    truncateLength() {
      return this.reviewsListConfig.breakpoints[this.currentLayoutIndex || 0].truncateLength
    }
  },
  methods: {
    fetchData: debounce(function cb(isPageChange) {
      if (this.currentSku && (this.offset === 0 || this.offset <= this.total)) {
        const limit =
          this.offset === 0 ? this.initialPageSize : this.pageSize
        if (reviewsListConfig.useShowMore && this.offset !== 0) {
          this.showMoreDisabled = true
        }
        const isFirstCall = this.currentSku !== this.previousSku
        relatedReviewsListService.relatedReviewsList({
          sku: this.currentSku,
          offset: this.offset,
          limit,
          sort: this.sortBy
        }).then((data) => {
          this.previousSku = this.currentSku
          this.total = data.total
          if (reviewsListConfig.useShowMore && this.offset !== 0) {
            data.reviews.forEach((review) => {
              this.reviews.push(review)
            })
            this.showMoreDisabled = false
          } else {
            this.reviews = data.reviews
          }
          if (isFirstCall) {
            this.$emit('relatedReviewsLoaded', this.reviews)
          } else {
            nextTick(() => {
              if (isPageChange) {
                // TT-11837 - focus header after scroll. safeFocus in case client hides the header
                const focusFn = () => domUtils.safeFocus(() => this.$refs.headingText)
                domUtils
                  .scrollUpToRef(this.$refs, 'relReviewsList', globalConfig.additionalPagingScroll)
                  .then(focusFn)
                  .catch(focusFn)
              }
              callTurnToConfigFn('relatedReviews', 'onUpdate')
            })
          }
        }).catch((e: RestError) => {
          logger.warn('Error loading reviews list widget', e)
        })
      }
    }, 100),
    openMediaModal(media, startingIndex = 0) {
      // passing it up one more level
      this.$emit('openMediaModal', media, startingIndex)
    },
    translateReview(review, showOriginal: boolean) {
      reviewsListUtils.translateReview(review, this.originalReviews, this.reviews, showOriginal)
    }
  },
  created() {
    // initialPageSize and pageSize are in PaginateMixin
    this.initialPageSize =
      reviewsListConfig.relatedInitialPageSize || reviewsListConfig.relatedPageSize
    this.pageSize = reviewsListConfig.relatedPageSize
    // setLayoutConfig method in GridLayoutDataMixin
    this.setLayoutsConfig(reviewsListConfig.breakpoints)
    this.fetchData()
  },
  watch: {
    currentSku() {
      // onQueryChange method in PaginateMixin
      this.onQueryChange()
    }
  }
}
</script>

<style lang="scss" src="./related-reviews-list.scss"></style>
